// ============================================================
//  CloudImagePicker — Upload to Supabase Storage or backend
//  Drop-in replacement for ImagePicker.jsx in admin pages
// ============================================================

import { useState, useRef } from "react";
import { Upload, X, Loader2, Link as LinkIcon } from "lucide-react";
import { uploadMedia } from "@/lib/cloudApi";
import { isSupabaseReady } from "@/lib/supabase";
import {
  compressImageFile,
  getUploadErrorMessage,
  normalizeAndValidateExternalImage,
  validateImageFile,
} from "@/lib/adminImageUtils";

export default function CloudImagePicker({
  value,
  onChange,
  label,
  testId,
  folder = "uploads",
  previewClassName = "",
  previewStyle,
}) {
  const [uploading, setUploading]   = useState(false);
  const [progress,  setProgress]    = useState(0);
  const [urlInput,  setUrlInput]    = useState(false);
  const [urlVal,    setUrlVal]      = useState("");
  const [error,     setError]       = useState("");
  const fileRef = useRef();

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    setUploading(true);
    setProgress(0);
    try {
      validateImageFile(file);
      const maxEdge = /cover|hero/i.test(folder) ? 3000 : 2000;
      const compressed = await compressImageFile(file, { maxEdge, quality: maxEdge > 2000 ? 0.86 : 0.84 });
      const result = await uploadMedia(compressed, folder, setProgress);
      const url = result?.url || result;
      onChange(url);
    } catch (e) {
      const message = getUploadErrorMessage(e);
      console.error("[ADMIN_IMAGE_UPLOAD_ERROR]", { label, folder, fileName: file.name, fileType: file.type, fileSize: file.size, message, error: e });
      setError(message);
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const applyUrl = async () => {
    setError("");
    try {
      const url = await normalizeAndValidateExternalImage(urlVal);
      onChange(url);
      setUrlVal("");
      setUrlInput(false);
    } catch (e) {
      console.error("[ADMIN_IMAGE_URL_ERROR]", { label, folder, input: urlVal, message: e.message });
      setError(e.message);
    }
  };

  const supLabel = isSupabaseReady()
    ? "Stored in Supabase (live on Vercel)"
    : "Stored in local backend";

  return (
    <div data-testid={testId}>
      {label && (
        <label className="eyebrow text-[10px] text-[#C99A45]/80 block mb-2">{label}</label>
      )}

      {/* Preview */}
      {value && !uploading && (
        <div className="relative w-full max-w-sm mb-3 rounded-2xl overflow-hidden border border-[#171717]/10 bg-white shadow-sm">
          <img
            src={value}
            alt="preview"
            className={`w-full h-44 ${previewClassName || "object-cover"}`}
            style={previewStyle}
          />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 bg-white/90 rounded-full p-2 text-[#b42318] hover:bg-[#b42318] hover:text-white transition shadow"
          >
            <X size={12} />
          </button>
        </div>
      )}

      {/* Upload zone */}
      {!uploading ? (
        <div
          className="rounded-2xl border border-dashed border-[#b8914a]/45 bg-[#fbfaf7] p-7 text-center cursor-pointer hover:border-[#b8914a] hover:bg-[#fffdf8] transition"
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileRef.current?.click()}
        >
          <div className="mx-auto h-11 w-11 rounded-full bg-[#b8914a]/10 flex items-center justify-center mb-3">
            <Upload size={19} className="text-[#b8914a]" />
          </div>
          <p className="text-sm text-[#171717] font-medium">Drop image here or browse</p>
          <p className="text-[11px] text-[#6f6a62] mt-1">{supLabel}</p>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </div>
      ) : (
        <div className="rounded-2xl border border-[#b8914a]/30 bg-white p-7 text-center">
          <Loader2 size={20} className="mx-auto text-[#C99A45] animate-spin mb-2" />
          <p className="text-xs text-[#6f6a62]">Uploading... {progress}%</p>
          <div className="mt-3 w-full bg-[#ece7dd] h-1.5 rounded-full overflow-hidden">
            <div className="h-1 bg-[#C99A45] transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {/* URL paste option */}
      <button
        type="button"
        className="mt-3 inline-flex items-center gap-1 text-[10px] text-[#b8914a] hover:text-[#171717] transition uppercase tracking-[0.16em]"
        onClick={() => setUrlInput(v => !v)}
      >
        <LinkIcon size={10} /> Paste URL instead
      </button>
      {urlInput && (
        <div className="mt-2 flex gap-2">
          <input
            type="url"
            value={urlVal}
            onChange={e => setUrlVal(e.target.value)}
            placeholder="https://..."
            className="admin-input flex-1 !py-2 text-xs"
          />
          <button
            type="button"
            onClick={applyUrl}
            className="text-[10px] eyebrow text-[#C99A45] border border-[#C99A45]/40 px-3 py-1"
          >
            Use
          </button>
        </div>
      )}
      {error && (
        <div className="mt-3 rounded-xl border border-red-500/25 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}
